<template>
    <div
        id="ranking"
        class="m-0 d-flex flex-column bd-highlight text-start"
        :aria-label="$route.name.includes('exchange') ? $t('product-ranking') : $t('ranking-section')">
        <div
            v-for="(ranking, index) in rankingData"
            :key="ranking.place"
            data-aos="show-from-bottom"
            data-aos-anchor="#ranking"
            data-aos-easing="from-bottom-easing"
            :data-aos-delay="200 * index"
            :aria-label="$t('rank')+' '+ranking.place+' '+ $t(ranking.description.text, { n: '' })">
            <div
                class="place col-12 m-0 row"
                :class="{ 'no-pointer': ranking.id === 'CA' }"
                :style="'--place-color:' + (ranking.id === 'CA' ? rankingColors['canada'] : rankingColors[`place-${index + 1}`])"
                @click="moreInfo(index)">
                <div class="position col-2 col-sm-2 col-md-2 col-lg-1 p-0 d-flex justify-content-center align-items-center text-center">
                    <h3 class="m-0" :aria-label="$t('rank') +' '+ ranking.place"> {{ ranking.place }} </h3>
                </div>
                <div class="data col-10 col-sm-10 col-md-10 col-lg-11 px-2 px-sm-2 px-md-1 px-lg-3 py-3">
                    <div class="d-flex justify-content-between">
                        <div class="infos pe-2 pe-sm-2 pe-md-2 pe-lg-3" :class="{limit: (ranking.icon && ranking.icon.type === 'thing')}">
                            <h3 class="m-0"
                                v-if="$i18n.locale === 'fr' || ranking.displayValue === 0 || categoryInfo.config.multiple === null"
                                :aria-label="categoryInfo.name === 'immigration' ? ranking.displayValue +' '+ $t('new-arrivals-average') : categoryInfo.config.prefix + ranking.displayValue + categoryInfo.config.suffix">
                                {{ vueNumberFormat(ranking.displayValue, {
                                        precision:      categoryInfo.config.precision,
                                        prefix:         categoryInfo.config.prefix,
                                        suffix:         categoryInfo.config.suffix,
                                        decimal:        categoryInfo.config.decimal,
                                        thousand:       $t('thousand-format'),
                                        acceptNegative: false,
                                        isInteger:      false
                                    })
                                }}
                            </h3>
                            <h3 class="m-0" v-else>
                                {{ (ranking.displayValue * categoryInfo.config.multiple) | numeral('$0.0a') }}
                            </h3>
                            <div class="country-name">
                                <p class="m-0 mb-1">
                                    {{ $t(ranking.description.text, {n: ''}) }}
                                </p>
                            </div>
                        </div>
                        <div
                            v-if="ranking.icon && ranking.icon.type"
                            :class="[(ranking.rating === null) ? 'align-self-center' : (index > 9) ? 'align-self-center' : null, ranking.icon.type]"
                        >
                            <img
                                :src="require(`@/assets/images/${ranking.icon.name}`)"
                                :height="( $screen.width > 992 ) ? ranking.icon.height : (ranking.icon.height > 25) ? (ranking.icon.height - 10) : ranking.icon.height"
                                alt=""
                            >
                        </div>
                    </div>
                    <div class="example col-12" :class="{
                        'description-down': placeShowedDetailsIndex === index,
                        'description-up':   placeShowedDetailsIndex !== index}"
                        v-if="(ranking.description.details && !withModal && animationsToggle) || (ranking.description.details && placeShowedDetailsIndex === index)"
                    >
                        <p class="m-0 d-block">
                            {{ `${$t('example')} ${$t(ranking.description.details)}` }}
                        </p>
                    </div>
                    <div
                        class="rating col-12"
                        v-if="ranking.rating && categoryInfo.name !== 'exchange'"
                        :aria-label="$t('amount-bar')">
                        <hr class="progress-bar" :style="`--width: ${(animationsToggle === true ? ranking.displayRating : ranking.rating)}%`">
                    </div>
                </div>
            </div>
            <div
                class="after-ten col-1 p-0 d-flex justify-content-center align-items-center text-center"
                v-if="( index === 9 && rankingData.length > 10)"
            >
                <p class="m-0 text-white"> ... </p>
            </div>
        </div>
    </div>
</template>
<script>
import { Default }     from '@/helpers/Default.js';
import { EventBus }    from '@/helpers/EventBus.js';

// Libraries
import anime from 'animejs/lib/anime.js';

export default {
    props: {
        rankingData: {
            /**
             * RankingData props.
             *
             * id:          { string }   The ID of the data (CA for canada)
             * description  { Object }   Can be country name or other {
             *                           - text:    The description text,
             *                           - plural:  True or False,
             *                           - details: More details about the ranking description (can be null) }.
             * place        { integer }  Place in ranking
             * type         { string }   Type of data ($, %, other)
             * value        { string }   The data value to show
             * rating       { integer }  Rating of each place (%) (for the bottom bare)
             * icon         { object }   Can be flag or thing {
             *                           - name:   name of the image,
             *                           - type:   flag or thing,
             *                           - height: can be 25 for normal flag, 50 for canada's flag, 80 for thing }
             *
             */
            type: [Array, Object]
        },
        withModal:    Boolean,
        categoryInfo: Object
    },
    data () {
        return {
            animationsToggle: Default.animationsToggle,
            rankingColors:   {
                'place-1':     '#dddf68',
                'place-2':     '#81cbea',
                'place-3':     '#ff9b9d',
                'place-4':     '#ebba34',
                'place-5':     '#eb8e34',
                'place-6':     '#87a0c4',
                'place-7':     '#cfcfcf',
                'place-8':     '#9897e8',
                'place-9':     '#c591d5',
                'place-10':    '#ef89a4',
                'canada':      '#f5f5f6'
            },
            placeShowedDetailsIndex: null,
            dataToCompareWithCanada: {},
            animationDelay: 2000
        };
    },
    mounted () {
        // Change compaing data.
        EventBus.$on('change-compare-with', this.changeComparingData);

        if (this.$session.has('animationsToggle')) {
            this.animationsToggle = this.$session.get('animationsToggle');
        }

        // Listen for OpenDyslexic font setting value
        EventBus.$on('settingsChanged', (setting) => {
            this[setting.name] = setting.value;
        });
    },
    watch: {
        rankingData () {
            if (this.rankingData === null) {
                return;
            }
            this.calculRatings();
        }
    },
    destroyed () {
        EventBus.$off('change-compare-with');
    },
    methods: {
        /**
         * Change the comparing data
         *
         * @param   {objec}   event    the Change comparing data event
         */
        changeComparingData (event) {
            var nextToShow = (event.sens === 'next') ? (event.index + 1) : (event.index - 1);
            if (this.rankingData[nextToShow].id === 'CA') {
                nextToShow = (event.sens === 'next') ? (nextToShow + 1) : (nextToShow - 1);
            }
            this.moreInfo(nextToShow);
        },

        /**
         * Show more information (popup or details section)
         *
         * @param    {integer}    index    the index of the place
         *
         * @returns   void
         */
        moreInfo (index) {
            if (!this.withModal) {
                if (this.placeShowedDetailsIndex === index) {
                    this.placeShowedDetailsIndex = null;
                } else {
                    this.placeShowedDetailsIndex = index;
                }
                return;
            }

            if (this.rankingData[index].id === 'CA') {
                return;
            }

            // Send data to parent to show comparison modal.
            this.dataToCompareWithCanada = {};
            this.rankingData[index].index      = index;
            this.rankingData[index].placeColor = this.rankingColors[`place-${index + 1}`];
            this.rankingData[index].lastOne    = (this.rankingData.length === (index + 1)) ? true : false;
            this.rankingData[index].firstOne   = (index === 0) ? true : false;
            this.dataToCompareWithCanada       = this.rankingData[index];
            this.$emit('showModal', this.dataToCompareWithCanada);
        },

        /**
         * Calcul the rating of each rank
         */
        calculRatings() {
            var totalValues = 0;
            for (let index = 0; index < this.rankingData.length; index++) {
                totalValues += this.rankingData[index].value;
            }

            this.rankingData.forEach(rank => {
                rank.rating = (rank.value * 100) / totalValues;
            });

            // Starts the count
            this.startCount();
        },

        /**
         * Update the displayValue of the situation with the ranking value
         * animate Ranking value
         * Animate progess bar
         */
        startCount () {
            this.rankingData.forEach((ranking) => {
                // init first
                ranking.displayValue  = 0;
                ranking.displayRating = 0;

                ranking.displayValue += ranking.value;

                // Set progress bar value to rating
                const timer = this.animationsToggle ? 2000 : 0;
                setTimeout(() => ranking.displayRating += ranking.rating, timer);

                const obj = { n: 0 };
                if (this.animationsToggle === true) {
                    anime({
                        targets:  obj,
                        n:        ranking.value,
                        round:    this.categoryInfo.config.round,
                        duration: 1000,
                        delay:    this.animationDelay,
                        easing:   'linear',
                        update:   () => {
                            ranking.displayValue = obj.n;
                        }
                    });
                }
            });
            // After the first execution, we want to reduce the delay to 500ms when we click on the radio buttons
            this.animationDelay = 500;
        }
    }
}
</script>
